import { memo, Ref } from 'react';
import { CallCardDetailsForm } from '@/components/callcard/CallCardDetailsForm';
import { CallCardComments } from '@/components/callcard/CallCardComments';
import { CallCardDisposition } from '@/components/callcard/CallCardDisposition';
import { OfficersAssigned } from '@/components/screens/OfficersAssigned';
import { useTheme } from '@/contexts/ThemeContext';
import { hasStringValue } from '@/utils/legacy-utils';

interface CallCardDetailsTabProps {
  CC: any;
  CCCntr: any;
  moduleType: any;
  isFormDisabled: boolean;
  callCardNotClosed: boolean;
  isEditingClosedCallCard: boolean;
  canConfirmOnSceneLocation?: boolean;
  canEditCallLocation?: boolean;
  callAlert: any;
  natureOfCallSopLink: string;
  clientLink: string;
  callLocationMapLink: string;
  patrolZoneNames: string[];
  isShowingClientAddressButton: boolean;
  natureOfCallOptions: any[];
  natureOfCallGroupBy?: string | null;
  clientOptions: any[];
  siteOptions: any[];
  dispositionOptions: any[];
  dispositionGroupBy?: string | null;
  officers: any[];
  availablePeople: any[];
  officersRef: Ref<any>;
  canSendCallComments: boolean;
  hasCallCommentUpdatePermission: boolean;
  onNatureChange: (e: { values: any[]; items: any[] }) => void;
  onClientChange: (e: { value: number | null; item: any }) => void;
  onSiteChange: (e: { values: any[]; items: any[] }) => void;
  onFieldChange: (data: { field: string; value: any }) => void;
  onLocationBlur: (event: any) => void;
  onClientAddressClick: () => void;
  onDisabledFieldClick: () => void;
  onCommentAddedToNew: (commentData: any) => void;
  onCommentUpdated: (data: { id: number; entry: string }) => void;
  onOfficersChange: (newOfficers: any[]) => void;
  onDispositionChange: (e: { value: any }) => void;
  onDispositionFieldChange: (data: { field: string; value: any }) => void;
  realtimeVersion?: number;
  priorityAutoChangeVersion?: number;
  onNatureOpen?: () => void;
  onNatureClose?: () => void;
}

export const CallCardDetailsTab = memo(function CallCardDetailsTab({
  CC,
  CCCntr,
  moduleType,
  isFormDisabled,
  callCardNotClosed,
  isEditingClosedCallCard,
  canConfirmOnSceneLocation = false,
  canEditCallLocation = false,
  callAlert,
  natureOfCallSopLink,
  clientLink,
  callLocationMapLink,
  patrolZoneNames,
  isShowingClientAddressButton,
  natureOfCallOptions,
  natureOfCallGroupBy = null,
  clientOptions,
  siteOptions,
  dispositionOptions,
  dispositionGroupBy = null,
  officers,
  availablePeople,
  officersRef,
  canSendCallComments,
  hasCallCommentUpdatePermission,
  onNatureChange,
  onClientChange,
  onSiteChange,
  onFieldChange,
  onLocationBlur,
  onClientAddressClick,
  onDisabledFieldClick,
  onCommentAddedToNew,
  onCommentUpdated,
  onOfficersChange,
  onDispositionChange,
  onDispositionFieldChange,
  priorityAutoChangeVersion,
  onNatureOpen,
  onNatureClose,
}: CallCardDetailsTabProps) {
  const { theme } = useTheme();

  return (
    <div className={`callcard-details-tab ${theme} ${isFormDisabled ? 'form-readonly' : ''} ${!callCardNotClosed && !isEditingClosedCallCard ? 'form-closed' : ''}`}>
      {/* Call Alert Banner */}
      {callAlert && hasStringValue(callAlert.message) && (
        <div className="call-alert-banner" style={{ borderColor: '#' + callAlert.borderColor }}>
          <div className="call-alert-content" style={{ backgroundColor: '#' + callAlert.backgroundColor, color: '#' + callAlert.textColor }}>
            {callAlert.message}
          </div>
        </div>
      )}

      {/* Main Details Grid */}
      <div className="details-grid">
        {/* Left Column: Call Details Form */}
        <div className="details-column-left">
          <CallCardDetailsForm
            callCard={CC}
            isReadOnly={isFormDisabled}
            callCardNotClosed={callCardNotClosed}
            isEditingClosedCallCard={isEditingClosedCallCard}
            canConfirmOnSceneLocation={canConfirmOnSceneLocation}
            canEditCallLocation={canEditCallLocation}
            natureOfCallSopLink={natureOfCallSopLink}
            clientLink={clientLink}
            callLocationMapLink={callLocationMapLink}
            patrolZoneNames={patrolZoneNames}
            isShowingClientAddressButton={isShowingClientAddressButton}
            natureOfCallOptions={natureOfCallOptions}
            natureOfCallGroupBy={natureOfCallGroupBy}
            clientOptions={clientOptions}
            siteOptions={siteOptions}
            onNatureChange={onNatureChange}
            onClientChange={onClientChange}
            onSiteChange={onSiteChange}
            onFieldChange={onFieldChange}
            onLocationBlur={onLocationBlur}
            onClientAddressClick={onClientAddressClick}
            onDisabledFieldClick={onDisabledFieldClick}
            priorityAutoChangeVersion={priorityAutoChangeVersion}
            onNatureOpen={onNatureOpen}
            onNatureClose={onNatureClose}
          />
        </div>

        {/* Right Column: Comments */}
        <div className="details-column-right">
          <CallCardComments
            callCardId={CC.id}
            comments={CCCntr.callComments}
            canSend={canSendCallComments}
            canUpdate={hasCallCommentUpdatePermission}
            isEditingClosedCallCard={isEditingClosedCallCard}
            onCommentAddedToNew={onCommentAddedToNew}
            onCommentUpdated={onCommentUpdated}
            onEditStarted={() => {}}
            onEditCancelled={() => {}}
            onDisabledFieldClick={onDisabledFieldClick}
          />
        </div>
      </div>

      {/* Officers Assigned */}
      <div className="officers-section">
        <OfficersAssigned
          ref={officersRef}
          officers={officers}
          people={availablePeople}
          currentEventId={CC.eventId}
          moduleType={moduleType}
          isReadOnly={isFormDisabled}
          onChange={onOfficersChange}
          onDisabledFieldClick={onDisabledFieldClick}
        />
      </div>

      {/* Call Disposition */}
      <CallCardDisposition
        callCard={CC}
        isReadOnly={isFormDisabled}
        callCardNotClosed={callCardNotClosed}
        isEditingClosedCallCard={isEditingClosedCallCard}
        dispositionOptions={dispositionOptions}
        dispositionGroupBy={dispositionGroupBy}
        onDispositionChange={onDispositionChange}
        onFieldChange={onDispositionFieldChange}
        onDisabledFieldClick={onDisabledFieldClick}
      />
    </div>
  );
});
